import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";

import { deleteTypeCurve } from "../reducers/typeCurveSlice";
import { Cancel, ExpandMore, Info } from "@mui/icons-material";
import { TypeCurveTransferList } from "./TypeCurveTransferList";
import { selectUser } from "../reducers/userSlice";
import { calculateTypeCurve } from "../helpers/calculateTypeCurve";
import uuid from "react-uuid";

import { selectLists, setListFilters } from "../reducers/listSlice";
import { selectWells } from "../reducers/wellSlice";
import { normalizeVolume } from "../helpers/normalizeVolume";
import { normalizePeakRate } from "../helpers/normalizePeakRate";
import { useSocket } from "../context/useSocket";
import { useSnackbar } from "notistack";

// const MONTH_LENGTH = 30.436875;
// const PRODUCTION_CUTOFF = 0.1;


export const EditTypeCurve = ({ listId, color, typeCurveClose, wells, typeCurve }) => {


  const user = useSelector(selectUser);
  const lists = useSelector(selectLists);
  const list = lists?.byId[listId];
  const filters = list?.filters || 0;

  const allWells = useSelector(selectWells);

  const { socket } = useSocket();

  const { enqueueSnackbar } = useSnackbar();

  let sourceWells = wells;
  const [typeCurveName, setTypeCurveName] = useState(typeCurve?.name || "New Type Curve");
  const [typeCurveDescription, setTypeCurveDescription] = useState(typeCurve?.description || "New Type Curve");
  const [typeCurveColor, setTypeCurveColor] = useState(typeCurve?.color || color); //eslint-disable-line
  const [consolidation, setConsolidation] = useState(typeCurve?.fullCycle ? 'production+forecast' : 'production');
  const [visibility, setVisibility] = useState(typeCurve?.public ? "public" : "private");
  const [completedLength, setCompletedLength] = useState(typeCurve?.details?.length || 2000);
  const [clusterSpacing, setClusterSpacing] = useState(typeCurve?.details?.spacing || 50);
  const [tpm, setTpm] = useState(typeCurve?.details?.tpm || 2.5);
  const [normalization, setNormalization] = useState(typeCurve?.normalization || 'off');
  const [normalizationPeakRate, setNormalizationPeakRate] = useState(typeCurve?.normalizationPeakRate || 'off');
  const [uwis, setUwis] = useState(typeCurve?.uwis || []);

  const dispatch = useDispatch();

  return (
    <Dialog 
      open={true} 
      onClose={typeCurveClose}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle id="form-dialog-title">{typeCurve?.id ? 'Edit' : 'New'} Type Curve</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="normal"
          id="name"
          label="Type Curve Name"
          placeholder="Type Curve Name"
          type="text"
          fullWidth
          value={typeCurveName}
          variant="outlined"
          onChange={e => setTypeCurveName(e.target.value)}
        />

        <TextField
          margin="normal"
          id="description"
          label="Description"
          placeholder="Description"
          type="text"
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          value={typeCurveDescription}
          variant="outlined"
          onChange={e => setTypeCurveDescription(e.target.value)}
        />

        <FormControl component="fieldset" style={{width: '100%', marginTop: 20}}>
          <FormLabel component="legend">Normalization Parameters</FormLabel>
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <TextField
              margin="normal"
              id="length"
              label="Completed Length (m)"
              placeholder="1000"
              type="number"
              value={completedLength}
              variant="outlined"
              style={{width: 200}}
              onChange={e => setCompletedLength(Number(e.target.value) || 0)}
            />
            <TextField
              margin="normal"
              id="clusterSpacing"
              label="Cluster Spacing (m)"
              placeholder="25"
              type="number"
              value={clusterSpacing}
              variant="outlined"
              style={{width: 200}}
              onChange={e => setClusterSpacing(Number(e.target.value) || 0)}
            />
            <TextField
              margin="normal"
              id="tpm"
              label="TPM (tonne/m)"
              placeholder="2.5"
              type="number"
              value={tpm}
              variant="outlined"
              style={{width: 200}}
              onChange={e => setTpm(Number(e.target.value) || 0)}
            />
          </div>
        </FormControl>

        <FormControl component="fieldset" style={{width: '100%', marginTop: 20}}>
          <FormLabel component="legend">Normalization Type</FormLabel>
          <ToggleButtonGroup
            value={normalization}
            exclusive
            onChange={updateNormalization}
            style={{width: '100%', marginTop: 10}}
          >
            <ToggleButton value="off" style={{width: '25%'}} color="primary">
              Off
            </ToggleButton>
            <ToggleButton value="length" style={{width: '25%'}} color="primary" disabled={!completedLength > 0}>
              Length
            </ToggleButton>
            <ToggleButton value="spacing" style={{width: '25%'}} color="primary" disabled={!clusterSpacing > 0}>
              Spacing
            </ToggleButton>
            <ToggleButton value="tpm" style={{width: '25%'}} color="primary" disabled={!tpm > 0}>
              TPM
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <FormControl component="fieldset" style={{width: '100%', marginTop: 20}}>
          <FormLabel component="legend">Normalize to Peak Rate</FormLabel>
          <ToggleButtonGroup
            value={normalizationPeakRate}
            exclusive
            onChange={updateNormalizationPeakRate}
            style={{width: '100%', marginTop: 10}}
          >
            <ToggleButton value="off" style={{width: '50%'}} color="primary">
              Off
            </ToggleButton>
            <ToggleButton value="on" style={{width: '50%'}} color="primary">
              On
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <FormControl component="fieldset" style={{width: '100%', marginTop: 20}}>
          <FormLabel component="legend">Consolidation</FormLabel>
          <ToggleButtonGroup
            value={consolidation}
            exclusive
            onChange={updateConsolidation}
            style={{width: '100%', marginTop: 10}}
          >
            <ToggleButton value="production" style={{width: '50%'}} color="primary">
              Production
            </ToggleButton>
            <ToggleButton value="production+forecast" style={{width: '50%'}} color="primary">
              Production + Forecast
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <div style={{display: 'flex', alignItems: 'center', margin: '10px 10px 20px 10px'}}>
          <Info color="primary" /><Typography component="span" style={{marginLeft: 10}}>
            {consolidation === 'production' ? 
              'Type curve will consolidate historic production for all selected wells and fit a forecast.'
            : 'Type curve will consolidate historic and forecast production for all selected wells and fit a forecast.'
            }
          </Typography>
        </div>

        <FormControl component="fieldset" style={{width: '100%', marginTop: 20, marginBottom: 20}}>
          <FormLabel component="legend">Visibility and Access</FormLabel>
          <ToggleButtonGroup
            value={visibility}
            exclusive
            onChange={updateVisibility}
            style={{width: '100%', marginTop: 10}}
          >
            <ToggleButton value="private" style={{width: '50%'}} color="primary">
              Private
            </ToggleButton>
            <ToggleButton value="public" style={{width: '50%'}} color="primary">
              Public
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Add Wells</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {Object.keys(filters)?.length > 0 && <Tooltip title="Remove Filters">
              <IconButton component="span" onClick={removeFilters}><Cancel /></IconButton>
            </Tooltip>}
            <Typography component="span">Active filters: {Object.keys(filters)?.length || 0}</Typography>
            <TypeCurveTransferList wells={sourceWells} uwis={uwis} setUwis={setUwis} filtersLength={Object.keys(filters)?.length} />
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions style={{display: 'flex', justifyContent: typeCurve?.id ? 'space-between' : 'flex-end'}}>
        {typeCurve?.id && 
        <Button onClick={(e) => handleDeleteTypeCurve(e, typeCurve.id)} color="secondary" disabled={(user?.value?.email !== typeCurve?.createdBy)}>
          Delete
        </Button>}
        <div>
          <Button onClick={typeCurveClose} color="primary" style={{marginRight: 10}}>
            Cancel
          </Button>
          <Button 
            onClick={() => typeCurveClick(uwis, user?.value?.email, (typeCurve?.id || undefined), (typeCurve?.uwi !== uwis || typeCurve?.normalization !== normalization ), typeCurve)} 
            color="primary" 
            variant="contained" 
            disabled={(typeCurveName?.length === 0) || (typeCurve?.createdBy && typeCurve?.createdBy !== user?.value?.email)}
          >
            {typeCurve?.id ? 'Update' : 'Generate'}
          </Button>
        </div>  
      </DialogActions>
    </Dialog>
  );

  // FUNCTIONS

  function removeFilters(e) { // remove all well filters
    e.stopPropagation();
    e.preventDefault();
    sourceWells = allWells;
    dispatch(setListFilters({listId: listId, filters: {}}));
  };

  function updateConsolidation(e, newValue) {
    newValue && setConsolidation(newValue);
  };

  function updateNormalization(e, newValue) {
    if (newValue) {
      setNormalization(newValue);
    };
  };

  function updateNormalizationPeakRate(e, newValue) {
    if (newValue) {
      setNormalizationPeakRate(newValue);
    };
  };

  function updateVisibility(e, newValue) {
    newValue && setVisibility(newValue);
  };

  async function typeCurveClick(uwis, email, id, recalculate, prevTypeCurve) {
    try {
      let production = [];
      let forecast = [];
      const excluded = [];
      
      for (let uwi of uwis) {
        const well = allWells.byUwi[uwi];
        const length = Number(well?.completions?.['Completed_Well_Length_m'] || well?.completions?.['lateral_length'] || 0);
        const number = Number(well?.completions?.['Number_of_Clusters'] || well?.completions?.['number_of_stages'] || 0);
        const tonnage = Number(well?.completions?.['Tonnage_Entry_Point'] || (well?.completions?.['proppant_per_stage']) || 0);

        if ((normalization === 'length' && !length > 0) || (normalization === 'spacing' && !number > 0) || (normalization === 'tpm' && !tonnage > 0)) {
          excluded.push(uwi);
          continue;
        };

        if (well?.production && well?.production?.fluids?.length > 0 && well?.status === 'processed') {
          let wellProduction = {...well.production};

          if (normalizationPeakRate === 'on') {
            wellProduction = normalizePeakRate(wellProduction, 'production');
          };

          Object.keys(wellProduction?.byFluid).forEach(fluid => {
            let fluidProduction = [];
            wellProduction.byFluid[fluid].forEach(p => {
              let volume = normalizeVolume(p.volume, normalization, {length: completedLength, spacing: clusterSpacing, tpm: tpm}, well?.completions);
              if (!isNaN(volume)) {
                fluidProduction.push({...p, volume: volume});
              };
            });
            wellProduction.byFluid = {...wellProduction.byFluid, [fluid]: fluidProduction};
          });
          production.push(wellProduction);
        };


        if (well?.forecast && Object.keys(well?.forecast)?.length > 0 && well?.status === 'processed') {
          // let wellForecast = { byFluid: {}, fluids: []};
          let wellForecast = {...well.forecast};

          if (normalizationPeakRate === 'on') { // normalize well forecast by peak rate
            wellForecast = normalizePeakRate(wellForecast, 'forecast');
          };

          Object.keys(wellForecast?.byFluid).forEach(fluid => {
            let fluidForecast = [];
            wellForecast.byFluid[fluid].forEach(f => {
              const p10 = normalizeVolume(f.volume?.p10, normalization, {length: completedLength, spacing: clusterSpacing, tpm: tpm}, well?.completions);
              const p50 = normalizeVolume(f.volume?.p50, normalization, {length: completedLength, spacing: clusterSpacing, tpm: tpm}, well?.completions);
              const p90 = normalizeVolume(f.volume?.p90, normalization, {length: completedLength, spacing: clusterSpacing, tpm: tpm}, well?.completions);
              if (!isNaN(p10) && !isNaN(p50) && !isNaN(p90)) {
                fluidForecast.push({...f, volume: { p10, p50, p90 }});
              };
            });
            wellForecast.byFluid = {...wellForecast.byFluid, [fluid]: fluidForecast};
          });
          forecast.push(wellForecast);
        };
      };

      if (excluded.length > 0) {
        enqueueSnackbar(`The following wells were excluded from the type curve due to missing completions data: ${excluded?.join(', ')}`, {
          variant: 'warning',
          persist: false
        });
      };


      if (production.length > 0) {
        enqueueSnackbar(`Processing type curve ${typeCurveName}...`, {
          variant: 'info',
          persist: true
        });

        const productionData = recalculate ? await calculateTypeCurve(production, forecast, consolidation === 'production+forecast') : null; // process production and forecast data for the type curve
        

        typeCurveClose();
        let typeCurve = {
          id: id || uuid(), 
          name: typeCurveName,
          description: typeCurveDescription,
          fullCycle: consolidation === 'production+forecast',
          public: visibility === 'public',
          createdBy: email, 
          createdOn: prevTypeCurve?.createdOn || new Date(), 
          lastModified: new Date(), 
          color: typeCurveColor, 
          uwis,
          production: (recalculate ? productionData : prevTypeCurve.production),
          forecast: (recalculate ? {} : prevTypeCurve.forecast),
          details: { length: completedLength > 0 ? completedLength: 0, spacing: clusterSpacing > 0 ? clusterSpacing: 0, tpm: tpm > 0 ? tpm : 0 },
          params: (recalculate ? null : prevTypeCurve.params),
          eur: (recalculate ? null : prevTypeCurve.eur),
          listId,
          normalization,
          normalizationPeakRate
        };
        
        if (recalculate) {
          if (socket && socket.readyState === 1) {
            socket.send(JSON.stringify({messageType: 'typeCurve', payload: typeCurve, userEmail: user?.value?.email}));
          };
        };
      };
  
      return;
    } catch (error) {
      console.log('Could not process request for new type curve');
      console.log(error);
      return;
    };
  };

  function handleDeleteTypeCurve(e, typeCurveId) {
    e.stopPropagation();
    dispatch(deleteTypeCurve({typeCurveId: typeCurveId}));
    typeCurveClose();
  };
};